// ---------------------------------------------------------------------------
// Anklickbares Dartboard als SVG
//
// Ersetzt den Ziffernblock. Der entscheidende Grund ist nicht die Optik: Beim
// automatischen Zaehlen meldet der Tracker einen TREFFERPUNKT, keine Zahl. Ein
// Ziffernblock kann den nicht darstellen - ein Board schon. Damit nutzen
// Handeingabe und Kameraerkennung dieselbe Ansicht, und eine Korrektur von
// Hand ist ein Klick auf die richtige Stelle statt eine Zahleneingabe.
//
// KOORDINATEN: absichtlich dieselbe Konvention wie tracking_engine/board:
//
//     Ursprung = Bullmitte, +x = rechts, +y = OBEN, Einheit Millimeter,
//     Winkel mathematisch (0 Grad = rechts, gegen den Uhrzeigersinn),
//     die 20 liegt bei 90 Grad.
//
// Der Tracker kann seine Millimeter also unveraendert uebergeben. Jede
// Umrechnung an der Schnittstelle waere eine Fehlerquelle, die niemand
// bemerkt, bis die Treffer systematisch verdreht sind.
//
// Die Radien stammen aus tracking_engine/configs/board.yaml. Wer sie dort
// aendert, muss sie hier nachziehen - deshalb stehen die Werte beisammen.
// ---------------------------------------------------------------------------

export const BOARD = {
  innerBull: 6.35, // Bullseye, 50
  outerBull: 15.9, // Bull, 25
  tripleInner: 99.0,
  tripleOuter: 107.0,
  doubleInner: 162.0,
  doubleOuter: 170.0, // Aussenkante = Spielflaeche
  // Reihenfolge im Uhrzeigersinn ab der 20 (oben)
  sectors: [20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5],
};

const SECTOR_DEG = 18;
// Rand ausserhalb der Spielflaeche: Platz fuer den Surround und fuer Wuerfe,
// die daneben gehen. Ohne diesen Rand gaebe es keine Flaeche fuer "Miss".
const VIEW = 210;

// ── Punktwertung ───────────────────────────────────────────────────────────

/** Wertung an einem Punkt in Millimetern (Bullmitte = 0,0, +y oben). */
export function scoreAt(xMm, yMm) {
  const r = Math.hypot(xMm, yMm);

  if (r <= BOARD.innerBull) {
    return { base: 50, multiplier: 1, points: 50, ring: "bullseye", label: "B-EYE" };
  }
  if (r <= BOARD.outerBull) {
    return { base: 25, multiplier: 1, points: 25, ring: "bull", label: "BULL" };
  }
  if (r > BOARD.doubleOuter) {
    return { base: 0, multiplier: 0, points: 0, ring: "miss", label: "MISS" };
  }

  // Winkel in Grad, 0 = rechts, gegen den Uhrzeigersinn
  const winkel = (Math.atan2(yMm, xMm) * 180) / Math.PI;
  // Die 20 liegt bei 90 Grad und ist Index 0. Von dort im UHRZEIGERSINN
  // weiterzaehlen - deshalb das Minus.
  const versatz = (((90 + SECTOR_DEG / 2 - winkel) % 360) + 360) % 360;
  const base = BOARD.sectors[Math.floor(versatz / SECTOR_DEG) % 20];

  let multiplier = 1;
  let ring = "single";
  if (r >= BOARD.tripleInner && r <= BOARD.tripleOuter) {
    multiplier = 3;
    ring = "triple";
  } else if (r >= BOARD.doubleInner) {
    multiplier = 2;
    ring = "double";
  }

  const prefix = { 1: "", 2: "D", 3: "T" }[multiplier];
  return { base, multiplier, points: base * multiplier, ring, label: prefix + base };
}

// ── Boardbereiche ──────────────────────────────────────────────────────────
//
// Gebraucht von obs.js (Szene je Boardbereich zoomen). Steht HIER, weil es
// Boardgeometrie ist: dieselbe Sektorreihenfolge, die auch scoreAt nutzt.
//
// Bis 14.08.2026 stand daneben `zielFeldAusCheckout(pfad)`, das aus einem
// Checkout-Weg wie "T20 T20 D20" das letzte Paket herausschnitt. Beide
// Abnehmer sind es losgeworden: Der Caller sagt keinen Weg mehr an, und
// obs.js zoomt erst, wenn der Rest SELBST mit einem Dart zu treffen ist -
// dann braucht es keine Checkout-Tabelle mehr, nur noch Rechnen.

/** Boardbereich eines Feldes: "oben" | "rechts" | "unten" | "links" | "mitte".
 *
 * Die Scheibe wird in vier Viertel zu je fuenf Sektoren geteilt, plus die
 * Mitte (Bull/Bullseye). Der Schnitt liegt bewusst bei 45 Grad zwischen
 * zwei Sektormitten (36 und 54 Grad) - kein Feld sitzt auf einer Grenze,
 * jedes gehoert eindeutig zu einem Viertel:
 *
 *   oben    20  1 18 12  5      rechts   4 13  6 10 15
 *   unten    2 17  3 19  7      links   16  8 11 14  9
 *
 * Abgeleitet aus BOARD.sectors statt aus einer zweiten Liste: Die
 * Reihenfolge ist bereits hier die Wahrheit, und eine Kopie waere die
 * naechste Stelle, die jemand zu pflegen vergisst.
 *
 * Eingabe ist ein Feldname wie "D20", "T19", "S5", "20", "BULL",
 * "B-EYE". Unbekanntes ergibt "" - der Aufrufer entscheidet dann selbst
 * (obs.js faellt auf die allgemeine Checkout-Szene zurueck). */
export function boardBereich(feld) {
  const text = String(feld || "").trim().toUpperCase();
  if (!text) return "";
  // Bull und Bullseye liegen in der Mitte - beide Schreibweisen, die im
  // Projekt vorkommen (Checkout-Tabelle "BULL", Anzeige "B-EYE").
  if (text === "BULL" || text === "B-EYE" || text === "BULLSEYE") return "mitte";

  const zahl = parseInt(text.replace(/^[SDT]/, ""), 10);
  if (zahl === 25 || zahl === 50) return "mitte";
  const index = BOARD.sectors.indexOf(zahl);
  if (index < 0) return "";

  // Sektormitte in Grad, im Uhrzeigersinn ab oben (Index 0 = die 20).
  const winkel = index * SECTOR_DEG;
  if (winkel < 45 || winkel >= 315) return "oben";
  if (winkel < 135) return "rechts";
  if (winkel < 225) return "unten";
  return "links";
}

// ── Zeichnen ───────────────────────────────────────────────────────────────

const NS = "http://www.w3.org/2000/svg";

function el(name, attrs) {
  const node = document.createElementNS(NS, name);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  return node;
}

/** Millimeter -> SVG. Das SVG hat y nach unten, das Board y nach oben. */
function toSvg(xMm, yMm) {
  return [xMm, -yMm];
}

function ringPfad(rInnen, rAussen, startDeg, endDeg) {
  const p = (r, deg) => {
    const rad = (deg * Math.PI) / 180;
    return toSvg(r * Math.cos(rad), r * Math.sin(rad));
  };
  const [x1, y1] = p(rAussen, startDeg);
  const [x2, y2] = p(rAussen, endDeg);
  const [x3, y3] = p(rInnen, endDeg);
  const [x4, y4] = p(rInnen, startDeg);
  // sweep 0, weil wachsende Winkel im SVG gegen den Uhrzeigersinn laufen
  return `M ${x1} ${y1} A ${rAussen} ${rAussen} 0 0 0 ${x2} ${y2}
          L ${x3} ${y3} A ${rInnen} ${rInnen} 0 0 1 ${x4} ${y4} Z`;
}

/**
 * Board in einen Container zeichnen.
 *
 * @param {HTMLElement} container
 * @param {{onThrow?: (treffer) => void, interactive?: boolean}} optionen
 */
export function createDartboard(container, optionen = {}) {
  const { onThrow = null, interactive = true } = optionen;
  container.innerHTML = "";

  const svg = el("svg", {
    viewBox: `${-VIEW} ${-VIEW} ${VIEW * 2} ${VIEW * 2}`,
    class: "dartboard" + (interactive ? " is-clickable" : ""),
    role: interactive ? "application" : "img",
    "aria-label": "Dartboard",
  });

  // Fassung: aeusserer Ring, Zahlenband, Spielflaeche. Ohne diese Ringe
  // schwebten die Segmente frei im Dunkeln - das Board hatte keine Kante und
  // wirkte unfertig. Die Reihenfolge ist die Zeichenreihenfolge: was zuerst
  // kommt, liegt hinten.
  svg.appendChild(el("circle", { r: VIEW - 2, cx: 0, cy: 0, class: "db-cabinet" }));
  svg.appendChild(el("circle", { r: BOARD.doubleOuter + 30, cx: 0, cy: 0, class: "db-ring" }));
  svg.appendChild(el("circle", { r: BOARD.doubleOuter, cx: 0, cy: 0, class: "db-plate" }));

  // Segmente: je Sektor vier Felder (innen-single, triple, aussen-single, double)
  BOARD.sectors.forEach((zahl, i) => {
    // Index 0 (die 20) ist um 90 Grad gedreht, weitere Sektoren im Uhrzeigersinn
    const mitte = 90 - i * SECTOR_DEG;
    const von = mitte - SECTOR_DEG / 2;
    const bis = mitte + SECTOR_DEG / 2;
    const hell = i % 2 === 0;

    const felder = [
      [BOARD.outerBull, BOARD.tripleInner, hell ? "db-a" : "db-b", 1],
      [BOARD.tripleInner, BOARD.tripleOuter, hell ? "db-red" : "db-green", 3],
      [BOARD.tripleOuter, BOARD.doubleInner, hell ? "db-a" : "db-b", 1],
      [BOARD.doubleInner, BOARD.doubleOuter, hell ? "db-red" : "db-green", 2],
    ];

    for (const [ri, ra, klasse, mult] of felder) {
      const pfad = el("path", {
        d: ringPfad(ri, ra, von, bis),
        class: "db-seg " + klasse,
      });
      pfad.dataset.base = zahl;
      pfad.dataset.mult = mult;
      svg.appendChild(pfad);
    }
  });

  // Die beiden Bulls tragen dieselben data-Attribute wie die Sektorfelder.
  // Nicht fuer die Wertung - die rechnet scoreAt() aus den Koordinaten -,
  // sondern damit zeigeZiel() sie mit DEMSELBEN Selektor findet. Ohne das
  // waere "Ziel BULL" der eine Sonderfall, den jeder Aufrufer selbst
  // behandeln muesste.
  const bullAussen = el("circle", { r: BOARD.outerBull, cx: 0, cy: 0, class: "db-seg db-green" });
  bullAussen.dataset.base = "25";
  bullAussen.dataset.mult = "1";
  svg.appendChild(bullAussen);
  const bullInnen = el("circle", { r: BOARD.innerBull, cx: 0, cy: 0, class: "db-seg db-red" });
  bullInnen.dataset.base = "50";
  bullInnen.dataset.mult = "1";
  svg.appendChild(bullInnen);

  // Zahlenkranz
  BOARD.sectors.forEach((zahl, i) => {
    const rad = ((90 - i * SECTOR_DEG) * Math.PI) / 180;
    const r = BOARD.doubleOuter + 20;
    const [x, y] = toSvg(r * Math.cos(rad), r * Math.sin(rad));
    const t = el("text", { x, y, class: "db-num", "text-anchor": "middle", dy: "0.36em" });
    t.textContent = String(zahl);
    svg.appendChild(t);
  });

  // Drahtringe: Sie liegen UEBER den Segmenten, sonst verdeckt das jeweils
  // spaeter gezeichnete Feld die Kante zur Haelfte.
  for (const r of [BOARD.outerBull, BOARD.tripleInner, BOARD.tripleOuter,
                   BOARD.doubleInner, BOARD.doubleOuter]) {
    svg.appendChild(el("circle", { r, cx: 0, cy: 0, class: "db-wire" }));
  }
  svg.appendChild(el("circle", { r: BOARD.doubleOuter + 30, cx: 0, cy: 0, class: "db-ring-line" }));

  // Ebene fuer die Treffer - zuletzt, damit sie ueber den Feldern liegt
  const marker = el("g", { class: "db-marks" });
  svg.appendChild(marker);
  // Ebene fuer die LIVE-Erkennung (pulsierender Punkt, 13.08.2026):
  // getrennt von den Treffern, weil sie bei jeder live-Nachricht des
  // Trackers komplett neu gezeichnet wird - die bestaetigten Marken
  // duerfen davon nichts merken. Sie liegt UEBER den Marken: ein
  // schwebender Punkt ist die juengste Information auf dem Board.
  const liveEbene = el("g", { class: "db-live" });
  svg.appendChild(liveEbene);
  container.appendChild(svg);

  function punktAusEvent(event) {
    // Bildpunkte in SVG-Koordinaten umrechnen. Ueber die Bildschirmmatrix,
    // damit Skalierung und Seitenverhaeltnis automatisch stimmen.
    const p = svg.createSVGPoint();
    p.x = event.clientX;
    p.y = event.clientY;
    const s = p.matrixTransform(svg.getScreenCTM().inverse());
    return { xMm: s.x, yMm: -s.y };
  }

  if (interactive) {
    svg.addEventListener("click", (event) => {
      const { xMm, yMm } = punktAusEvent(event);
      // Bewusst NICHT hier zeichnen: Ob der Treffer zaehlt, weiss nur der
      // Aufrufer - bei drei gesetzten Darts wird er verworfen. Vorher malte
      // das Board trotzdem, und bei jedem weiteren Klick kam ein Punkt dazu,
      // der zu keinem Dart gehoerte. Wer zeichnet, entscheidet onThrow.
      if (onThrow) onThrow({ ...scoreAt(xMm, yMm), xMm, yMm, quelle: "hand" });
    });
  }

  const api = {
    svg,
    /** Treffer einzeichnen. Koordinaten in Millimetern wie beim Tracker. */
    mark(xMm, yMm, beschriftung = "", quelle = "hand") {
      const [x, y] = toSvg(xMm, yMm);
      const g = el("g", { class: "db-mark db-mark-" + quelle });
      g.appendChild(el("circle", { cx: x, cy: y, r: 7, class: "db-mark-dot" }));
      g.appendChild(el("circle", { cx: x, cy: y, r: 13, class: "db-mark-ring" }));
      if (beschriftung) {
        const t = el("text", { x, y: y - 20, class: "db-mark-text", "text-anchor": "middle" });
        t.textContent = beschriftung;
        g.appendChild(t);
      }
      marker.appendChild(g);
      return g;
    },
    /** Letzten Treffer entfernen - fuer "Dart zurueck". */
    undo() {
      if (marker.lastChild) marker.removeChild(marker.lastChild);
    },
    clear() {
      marker.innerHTML = "";
      liveEbene.innerHTML = "";
    },
    /** Live erkannte, noch ungewertete Darts anzeigen (Tracker-Nachricht
     *  "live"). Ersetzt den kompletten Live-Bestand - eine leere Liste
     *  loescht ihn. Performant: je Dart zwei Kreise, kein Neuaufbau des
     *  Boards, und das Pulsieren macht CSS, nicht JavaScript. */
    setLive(darts) {
      liveEbene.innerHTML = "";
      for (const d of darts || []) {
        const [x, y] = toSvg(Number(d.x_mm ?? d.xMm), Number(d.y_mm ?? d.yMm));
        if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
        const g2 = el("g", { class: "db-live-dart" });
        g2.appendChild(el("circle", { cx: x, cy: y, r: 11, class: "db-live-ring" }));
        g2.appendChild(el("circle", { cx: x, cy: y, r: 5, class: "db-live-dot" }));
        liveEbene.appendChild(g2);
      }
    },
    /** Zielfelder hervorheben (Bob's 27, RTW).
     *
     *  Ersetzt das stilisierte Board aus ui.js, das genau dafuer da war. Zwei
     *  Scheiben nebeneinander waeren zwei Wahrheiten darueber, wo ein Feld
     *  liegt - und die stilisierte ist nicht masshaltig (Bull 7/16 statt
     *  6,35/15,9 mm). Der Tracker rechnet in DIESER Geometrie.
     *
     *  @param {Array<{base:number, mult?:number}>|null} felder
     *         mult weggelassen = jeder Ring dieser Zahl.
     *  @returns {number} Anzahl hervorgehobener Felder - damit ein Test
     *         nachsehen kann, ob das Ziel wirklich getroffen wurde. */
    zeigeZiel(felder) {
      for (const n of svg.querySelectorAll(".is-ziel")) n.classList.remove("is-ziel");
      let anzahl = 0;
      for (const f of felder || []) {
        const base = Number(f && f.base);
        if (!Number.isFinite(base)) continue;
        const mult = f.mult == null ? null : Number(f.mult);
        const auswahl =
          `[data-base="${base}"]` + (mult == null ? "" : `[data-mult="${mult}"]`);
        for (const n of svg.querySelectorAll(auswahl)) {
          n.classList.add("is-ziel");
          anzahl++;
        }
      }
      return anzahl;
    },
    get count() {
      return marker.childElementCount;
    },
  };
  return api;
}

Object.assign(window, {
  createDartboard,
  scoreAt,
  BOARD,
  boardBereich,
});
export default {
  createDartboard,
  scoreAt,
  BOARD,
  boardBereich,
};
